import { Component, Input, OnInit, ElementRef, ViewChild, Output, EventEmitter, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule, Validators, FormBuilder, FormArray, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { CalendarModule } from 'primeng/calendar';
import { FileUploadModule } from 'primeng/fileupload';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { EducationalLevelEnum } from '../../../../models/enums/EducationLevelEnum';
import { OnboardingService } from '../../../../services/onboarding/onboarding.service';
import { UtilityService } from '../../../../services/utility/utility.service';
import { StudentEducationRequest, StudentEducationResponse } from '../../../../models/onboarding/student-education';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { BlockUIModule } from 'primeng/blockui';
import { KeyFilterModule } from 'primeng/keyfilter';
import { LinkdindetailsFetchComponent, FieldStatus } from '../linkdindetails-fetch/linkdindetails-fetch.component';
import { validateFile } from '../../../../direct-apply/shared/validators/form-validation.utils';

export interface UploadedFileItem {
  file?: File;
  previewUrl: string | SafeResourceUrl;
  name: string;
  type?: string;
  base64Data?: string;
}

export interface DocumentParseData {
  jobId: string;
  status: 'pending' | 'parsing' | 'completed' | 'error';
  extractedData?: any;
  pollCount?: number;
}

@Component({
  selector: 'app-education-info',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    CalendarModule,
    FileUploadModule,
    DividerModule,
    DropdownModule,
    ButtonModule,
    DialogModule,
    ToastModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    BlockUIModule,
    KeyFilterModule,
    LinkdindetailsFetchComponent
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './education-info.component.html',
  styleUrls: ['./education-info.component.scss']
})
export class EducationInfoComponent implements OnInit, OnDestroy {
  @Input() parentForm!: FormGroup;
  @Output() stepSaved = new EventEmitter<void>();
  @Output() saveFailed = new EventEmitter<void>();
  @Output() stepStatusChanged = new EventEmitter<boolean>();

  educationInfoForm!: FormGroup;
  alphaSpaceRegex: RegExp = /^[a-zA-Z\s]+$/;
  numericRegex: RegExp = /^[0-9]+(\.[0-9]+)?$/;

  @ViewChild('hiddenFileInput') hiddenFileInput!: ElementRef<HTMLInputElement>;
  currentUploadIndex: number = -1;
  displayPreviewDialog: boolean = false;
  selectedPreviewItem: UploadedFileItem | null = null;
  safePdfUrl: SafeResourceUrl | null = null;
  isPreviewLoading: boolean = false;

  // Document-reading modal state
  @ViewChild(LinkdindetailsFetchComponent) docReadingModal!: LinkdindetailsFetchComponent;
  isDocModalVisible: boolean = false;
  docModalFileName: string = '';
  activeDocModalIndex: number = -1;
  /** Temporarily holds extracted data until the user clicks Continue in the modal */
  private pendingExtractedData: any = null;

  /** Fields shown inside the modal while an education transcript is being parsed */
  readonly educationDocFields: FieldStatus[] = [
    { id: 'edu-f0', name: 'Education Level', status: 'found', statusId: 'edu-s0' },
    { id: 'edu-f1', name: 'School Name', status: 'found', statusId: 'edu-s1' },
    { id: 'edu-f2', name: 'Board & Subjects', status: 'reading', statusId: 'edu-s2' },
    { id: 'edu-f3', name: 'Grades & Score', status: 'queued', statusId: 'edu-s3' }
  ];

  Marks: { id: number | null; Subject: string; Grade: string; saving: boolean }[] = [];
  EducationalLevelEnum = EducationalLevelEnum;
  isLoading: boolean = false;
  currentYear: number = new Date().getFullYear() + 7;

  // Document Parser Tracking
  parseJobData: DocumentParseData[] = [];
  private pollTimeouts: any[] = []; // Track timeouts for cleanup

  levelOptions = [
    { label: 'Grade 10', value: EducationalLevelEnum.Grade10 },
    { label: 'Grade 11', value: EducationalLevelEnum.Grade11 },
    { label: 'Grade 12 / Predicted', value: EducationalLevelEnum.Grade12 },
    { label: 'Bachelors', value: EducationalLevelEnum.Bachelors },
    { label: 'Masters', value: EducationalLevelEnum.Masters }
  ];

  boardOptions: { label: string; value: string }[] = [];
  countryOptions: { label: string; value: string }[] = [];

  constructor(
    private fb: FormBuilder,
    private onboardingService: OnboardingService,
    private utilityService: UtilityService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit() {
    this.educationInfoForm = this.fb.group({
      entries: this.fb.array([])
    });

    this.entries.valueChanges.subscribe(() => {
      this.checkStepStatus();
    });

    this.parentForm.setControl('educationInfo', this.educationInfoForm);
    this.isLoading = true;

    // Initialize parse tracking array
    this.initializeParseTracking();

    forkJoin({
      boards: this.onboardingService.getBoards().pipe(catchError(() => of([]))),
      countries: this.utilityService.getCountries().pipe(catchError(() => of({ result: [] })))
    }).subscribe({
      next: (res) => {
        this.boardOptions = res.boards.map(board => ({ label: board.name, value: board.id.toString() }));
        if (res.countries.result) {
          this.countryOptions = res.countries.result.map(c => ({ label: c.name, value: c.name }));
        }
        this.loadExistingData();
      },
      error: () => this.loadExistingData()
    });
  }

  ngOnDestroy() {
    // Clear all polling timeouts
    this.pollTimeouts.forEach(timeout => clearTimeout(timeout));
  }

  /**
   * Initialize parse job tracking array
   */
  initializeParseTracking() {
    for (let i = 0; i < 10; i++) {
      this.parseJobData[i] = {
        jobId: '',
        status: 'pending',
        pollCount: 0
      };
    }
  }

  loadExistingData() {
    this.isLoading = true;
    this.onboardingService.getEducations().subscribe({
      next: (res: any) => {
        const results = res.result || res.Result || [];
        if (results.length > 0) {
          this.entries.clear();
          this.Marks = [];
          results.forEach((record: any) => this.patchEntryFromResponse(record));
        } else if (this.entries.length === 0) {
          this.addDefaultEntries();
        }
        this.isLoading = false;
        this.checkStepStatus();
      },
      error: () => {
        if (this.entries.length === 0) this.addDefaultEntries();
        this.isLoading = false;
        this.checkStepStatus();
      }
    });
  }

  addDefaultEntries() {
    const group = this.createEntryGroup();
    this.setupLevelValidation(group);
    this.entries.push(group);
    this.Marks.push({ id: null, Subject: '', Grade: '', saving: false });
  }

  addEntryWithLevel(level: EducationalLevelEnum) {
    const group = this.createEntryGroup();
    this.setupLevelValidation(group);
    group.get('level')?.setValue(level);
    this.entries.push(group);
    this.Marks.push({ id: null, Subject: '', Grade: '', saving: false });
  }

  checkStepStatus() {
    const allSaved = this.entries.length > 0 && 
                     this.Marks.every(m => m.id !== null) &&
                     this.entries.controls.every(c => !c.dirty);
    this.stepStatusChanged.emit(allSaved);
  }

  /**
   * ✓ FIX: Extract year numbers from ISO date strings
   * When API returns dates like "2020-01-01T00:00:00.000Z",
   * extract the YEAR number (2020) instead of creating Date objects
   */
  patchEntryFromResponse(record: any) {
    const group = this.createEntryGroup();
    this.setupLevelValidation(group);

    const recordId = record.id || record.Id;
    let chips = record.subjectAndGrades || record.SubjectAndGrades || [];

    chips = chips.map((c: any) => ({
      SubjectName: c.subjectName || c.SubjectName || '',
      Grade: c.grade || c.Grade || ''
    }));

    const attendedFromStr = record.attendedFrom || record.AttendedFrom;
    const attendedToStr = record.attendedTo || record.AttendedTo;

    // ✓ FIX: Extract YEAR number from ISO date string
    let attendedFrom = null;
    let attendedTo = null;

    if (attendedFromStr) {
      const fromDate = new Date(attendedFromStr);
      if (!isNaN(fromDate.getTime())) {
        attendedFrom = fromDate.getFullYear();
      }
    }

    if (attendedToStr) {
      const toDate = new Date(attendedToStr);
      if (!isNaN(toDate.getTime())) {
        attendedTo = toDate.getFullYear();
      }
    }

    group.patchValue({
      level: record.educationLevel ?? record.EducationLevel,
      schoolName: record.schoolName || record.SchoolName || '',
      city: record.city || record.City || '',
      country: record.country || record.Country || '',
      attendedFrom: attendedFrom,
      attendedTo: attendedTo,
      boardType: (record.boardId ?? record.BoardId)?.toString() ?? '',
      subjectsGrades: chips,
      percentage: record.totalGrade ?? record.TotalGrade ?? null,
      universityName: record.schoolName || record.SchoolName || '',
      degreeName: record.degreeName || record.DegreeName || '',
      majors: record.majors || record.Majors || '',
      document: []
    });

    const docUrl = record.documentUrl || record.DocumentUrl;
    if (docUrl) {
      const urlWithoutParams = docUrl.split('?')[0];
      const fileName = urlWithoutParams.split('/').pop() || 'document';
      const type = urlWithoutParams.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg';

      const existingDoc: UploadedFileItem = {
        name: fileName,
        previewUrl: docUrl,
        type: type,
        base64Data: docUrl
      };
      group.get('document')?.setValue([existingDoc]);
      group.get('document')?.setErrors(null);
    }

    this.entries.push(group);
    const index = this.entries.length - 1;
    this.Marks.push({ id: recordId ?? null, Subject: '', Grade: '', saving: false });
  }

  setupLevelValidation(group: FormGroup) {
    group.get('level')?.valueChanges.subscribe(level => {
      const schoolName = group.get('schoolName');
      const uniName = group.get('universityName');
      const degreeName = group.get('degreeName');
      const majors = group.get('majors');
      const boardType = group.get('boardType');
      const subjectsGrades = group.get('subjectsGrades');
      const percentage = group.get('percentage');

      group.patchValue({
        schoolName: '',
        city: '',
        country: '',
        attendedFrom: null,
        attendedTo: null,
        boardType: '',
        subjectsGrades: [],
        percentage: null,
        universityName: '',
        degreeName: '',
        majors: '',
        document: []
      }, { emitEvent: false });

      if (level === EducationalLevelEnum.Bachelors || level === EducationalLevelEnum.Masters) {
        schoolName?.setValidators([Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]);
        boardType?.clearValidators();
        subjectsGrades?.clearValidators();
        percentage?.clearValidators();

        uniName?.setValidators([Validators.required, Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]);
        degreeName?.setValidators([Validators.required, Validators.pattern(/^[a-zA-Z\s]+$/)]);
        majors?.setValidators([Validators.required, Validators.pattern(/^[a-zA-Z\s]+$/)]);
      } else if (level !== null) {
        uniName?.setValidators([Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]);
        degreeName?.setValidators([Validators.pattern(/^[a-zA-Z\s]+$/)]);
        majors?.setValidators([Validators.pattern(/^[a-zA-Z\s]+$/)]);

        schoolName?.setValidators([Validators.required, Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]);
        boardType?.setValidators([Validators.required]);
        subjectsGrades?.setValidators([Validators.required]);
        percentage?.setValidators([Validators.required, Validators.pattern(this.numericRegex)]);
      }

      schoolName?.updateValueAndValidity();
      boardType?.updateValueAndValidity();
      subjectsGrades?.updateValueAndValidity();
      percentage?.updateValueAndValidity();
      uniName?.updateValueAndValidity();
      degreeName?.updateValueAndValidity();
      majors?.updateValueAndValidity();

      group.markAsUntouched();
      group.markAsPristine();
    });
  }

  get entries(): FormArray {
    return this.educationInfoForm.get('entries') as FormArray;
  }

  createEntryGroup(): FormGroup {
    return this.fb.group({
      level: [null, Validators.required],
      schoolName: ['', [Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]],
      city: ['', [Validators.required, Validators.pattern(/^[a-zA-Z\s]+$/)]],
      country: ['', Validators.required],
      attendedFrom: [null, Validators.required],
      attendedTo: [null, Validators.required],
      boardType: [''],
      subjectsGrades: [[]],
      percentage: [null, [Validators.required, Validators.pattern(this.numericRegex)]],
      universityName: ['', [Validators.pattern(/^[a-zA-Z\s\.\,\'-]+$/)]],
      degreeName: ['', [Validators.pattern(/^[a-zA-Z\s]+$/)]],
      majors: ['', [Validators.pattern(/^[a-zA-Z\s]+$/)]],
      document: [[]]
    }, { validators: [this.dateRangeValidator()] });
  }

  dateRangeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const fromCtrl = group.get('attendedFrom');
      const toCtrl = group.get('attendedTo');
      if (!fromCtrl || !toCtrl) return null;

      const fromVal = fromCtrl.value;
      const toVal = toCtrl.value;
      const currentYear = new Date().getFullYear();

      if (fromVal && !isNaN(Number(fromVal))) {
        const fromYear = Number(fromVal);
        if (fromYear > currentYear) {
          fromCtrl.setErrors({ ...fromCtrl.errors, yearFuture: true });
        } else {
          if (fromCtrl.hasError('yearFuture')) {
            const errs = { ...fromCtrl.errors };
            delete errs['yearFuture'];
            fromCtrl.setErrors(Object.keys(errs).length ? errs : null);
          }
        }

        if (toVal && !isNaN(Number(toVal))) {
          const toYear = Number(toVal);
          if (toYear < fromYear) {
            toCtrl.setErrors({ ...toCtrl.errors, dateRangeInvalid: true });
          } else {
            if (toCtrl.hasError('dateRangeInvalid')) {
              const errs = { ...toCtrl.errors };
              delete errs['dateRangeInvalid'];
              toCtrl.setErrors(Object.keys(errs).length ? errs : null);
            }
          }
        }
      }
      return null;
    };
  }

  addEntry() {
    const group = this.createEntryGroup();
    this.setupLevelValidation(group);
    this.entries.push(group);
    this.Marks.push({ id: null, Subject: '', Grade: '', saving: false });
    this.checkStepStatus();
  }

  removeEntry(index: number) {
    const id = this.Marks[index].id;

    const doRemoveInUI = () => {
      if (this.entries.length > 1) {
        this.Marks.splice(index, 1);
        this.entries.removeAt(index);
      } else {
        this.entries.at(0).reset();
        this.Marks[0] = { id: null, Subject: '', Grade: '', saving: false };
      }
      this.checkStepStatus();
    };

    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this education record?',
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      acceptButtonStyleClass: 'p-button-primary custom-confirm-yes',
      rejectButtonStyleClass: 'p-button-primary custom-confirm-no',
      accept: () => {
        if (id !== null) {
          this.onboardingService.deleteEducation(id).subscribe({
            next: () => {
              this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Education record removed.' });
              doRemoveInUI();
            },
            error: () => {
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete record from server.' });
            }
          });
        } else {
          doRemoveInUI();
        }
      }
    });
  }

  saveData(): void {
    const unsavedIndexes = this.entries.controls.map((_, i) => i).filter(i => this.Marks[i].id === null || this.entries.at(i).dirty);

    if (unsavedIndexes.length === 0) {
      this.stepSaved.emit();
      return;
    }

    let invalid = false;
    unsavedIndexes.forEach(i => {
      this.entries.at(i).markAllAsTouched();
      if (this.entries.at(i).invalid) invalid = true;
    });

    if (invalid) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.', life: 5000 });
      this.saveFailed.emit();
      return;
    }

    const promises = unsavedIndexes.map(i => this.persistEntry(i));
    Promise.all(promises).then(results => {
      if (results.every(Boolean)) {
        this.stepSaved.emit();
      } else {
        this.saveFailed.emit();
      }
    });
  }

  buildPayload(index: number): StudentEducationRequest {
    const entry = this.entries.at(index).value;
    const chips = entry.subjectsGrades || [];
    const subjectAndGrades = chips as { SubjectName: string; Grade: string }[];

    // attendedFrom and attendedTo are year numbers; convert to ISO dates (Jan 1st of that year)
    const attendedFrom = entry.attendedFrom ? new Date(entry.attendedFrom, 0, 1).toISOString() : null;
    const attendedTo = entry.attendedTo ? new Date(entry.attendedTo, 0, 1).toISOString() : null;

    const isBachelors = entry.level === EducationalLevelEnum.Bachelors || entry.level === EducationalLevelEnum.Masters;

    return {
      EducationLevel: entry.level,
      SchoolName: isBachelors ? entry.universityName : entry.schoolName,
      City: entry.city || '',
      Country: entry.country || '',
      AttendedFrom: attendedFrom,
      AttendedTo: attendedTo,
      // If it's a Bachelor's degree, exclude high school fields
      BoardId: isBachelors ? null : (entry.boardType ? Number(entry.boardType) : null),
      BoardName: isBachelors ? null : (this.boardOptions.find(b => b.value === entry.boardType)?.label || null),
      SubjectAndGrades: isBachelors ? [] : subjectAndGrades,
      TotalGrade: isBachelors ? '' : (entry.percentage?.toString() || ''),
      DegreeName: isBachelors ? (entry.degreeName || null) : null,
      Majors: isBachelors ? (entry.majors || null) : null,
      DocumentUrl: this.extractDocUrl(this.getUploadedFiles(index))
    } as StudentEducationRequest;
  }

  extractDocUrl(files: UploadedFileItem[]): string {
    if (!files || files.length === 0) return '';
    const item = files[0];
    return (item.previewUrl as string) || (item.base64Data as string) || '';
  }

  saveEntry(index: number) {
    void this.persistEntry(index);
  }

  private persistEntry(index: number): Promise<boolean> {
    const entry = this.entries.at(index);
    if (!entry) return Promise.resolve(false);
    const existingId = this.Marks[index].id;

    if (existingId !== null && !entry.dirty) {
      this.messageService.add({ severity: 'info', summary: 'No Changes', detail: 'No changes detected. Nothing to update.' });
      return Promise.resolve(true);
    }

    entry.markAllAsTouched();
    if (entry.invalid) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.', life: 5000 });
      return Promise.resolve(false);
    }

    this.isLoading = true;
    this.Marks[index].saving = true;
    const payload = this.buildPayload(index);
    const formData = new FormData();

    if (existingId !== null) {
      formData.append('Id', existingId.toString());
    }
    formData.append('EducationLevel', payload.EducationLevel.toString());
    formData.append('SchoolName', payload.SchoolName || '');
    formData.append('City', payload.City || '');
    formData.append('Country', payload.Country || '');
    formData.append('AttendedFrom', payload.AttendedFrom || '');
    formData.append('AttendedTo', payload.AttendedTo || '');

    if (payload.BoardId !== null && payload.BoardId !== undefined) {
      formData.append('BoardId', payload.BoardId.toString());
    }
    if (payload.BoardName !== null && payload.BoardName !== undefined) {
      formData.append('BoardName', payload.BoardName);
    }

    formData.append('TotalGrade', payload.TotalGrade || 'N/A');
    if (payload.DegreeName) {
      formData.append('DegreeName', payload.DegreeName);
    }
    if (payload.Majors) {
      formData.append('Majors', payload.Majors);
    }
    formData.append('SubjectAndGrades', JSON.stringify(payload.SubjectAndGrades || []));

    const uploadedFiles = this.getUploadedFiles(index);
    if (uploadedFiles.length === 0 && existingId !== null) {
      formData.append('RemoveDocument', 'true');
    } else {
      uploadedFiles.forEach((f: any) => {
        if (f.file instanceof File) {
          formData.append('DocumentUrl', f.file, f.name);
        }
      });
    }

    return new Promise((resolve) => {
      if (existingId !== null) {
        this.onboardingService.updateEducation(existingId, formData).subscribe({
          next: (res: any) => {
            this.Marks[index].saving = false;
            const newId = res?.id || res?.result?.id;
            if (newId) {
              this.Marks[index].id = newId;
              this.entries.at(index).get('id')?.setValue(newId);
            }
            entry.markAsPristine();
            this.isLoading = false;
            this.messageService.add({ severity: 'success', summary: 'Updated', detail: 'Education record updated.' });
            this.checkStepStatus();
            resolve(true);
          },
          error: (err) => {
            this.Marks[index].saving = false;
            this.isLoading = false;
            console.error('Update failed:', err);
            let errorMessage = 'Failed to update education record.';

            if (err?.error?.errors) {
              const errorMessages = Object.values<string[]>(err.error.errors).flat().join(' ');
              if (errorMessages) {
                errorMessage = errorMessages;
              }
            } else if (err?.error?.message) {
              errorMessage = err.error.message;
            }

            this.messageService.add({ severity: 'error', summary: 'Error', detail: errorMessage, life: 5000 });
            resolve(false);
          }
        });
      } else {
        this.onboardingService.createEducation(formData).subscribe({
          next: (res: any) => {
            this.Marks[index].saving = false;
            const newId = res?.id || res?.result?.id;
            if (newId) {
              this.Marks[index].id = newId;
              this.entries.at(index).get('id')?.setValue(newId);
            }
            entry.markAsPristine();
            this.isLoading = false;
            this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Education record saved.' });
            this.checkStepStatus();
            resolve(true);
          },
          error: (err) => {
            this.Marks[index].saving = false;
            this.isLoading = false;
            console.error('Save failed:', err);
            let errorMessage = 'Failed to save education record.';

            if (err?.error?.errors) {
              const errorMessages = Object.values<string[]>(err.error.errors).flat().join(' ');
              if (errorMessages) {
                errorMessage = errorMessages;
              }
            } else if (err?.error?.message) {
              errorMessage = err.error.message;
            }

            this.messageService.add({ severity: 'error', summary: 'Error', detail: errorMessage, life: 5000 });
            resolve(false);
          }
        });
      }
    });
  }

  addSubjectGrade(entryIndex: number) {
    const subject = (this.Marks[entryIndex].Subject || '').toString().trim();
    const mark = (this.Marks[entryIndex].Grade || '').toString().trim();
    if (!subject || !mark) return;

    const chip = { SubjectName: subject, Grade: mark };
    const control = this.entries.at(entryIndex)?.get('subjectsGrades');
    if (control) {
      const current = control.value || [];
      control.setValue([...current, chip]);
    }
    this.Marks[entryIndex].Subject = '';
    this.Marks[entryIndex].Grade = '';
  }

  removeSubjectGrade(entryIndex: number, chipIndex: number) {
    const control = this.entries.at(entryIndex)?.get('subjectsGrades');
    if (control) {
      const current = [...(control.value || [])];
      current.splice(chipIndex, 1);
      control.setValue(current);
    }
  }

  getSubjectGrades(entryIndex: number): string[] {
    const val = this.entries.at(entryIndex)?.get('subjectsGrades')?.value;
    return Array.isArray(val)
      ? val.map((sg: any) => `${sg.SubjectName} — ${sg.Grade}`)
      : [];
  }

  // ============================================
  // DOCUMENT PARSER METHODS
  // ============================================

  /**
   * STEP 1: User uploads document → Process and Parse
   */
  onFileUpload(event: any, index: number) {
    if (event.files && event.files.length > 0) {
      const file = event.files[0];
      const check = validateFile(file);
      if (!check.valid) {
        this.messageService.add({ severity: 'error', summary: 'Invalid File', detail: check.message });
        return;
      }
      this.processFileAndParse(file, index);
    }
  }

  /**
   * Show file preview immediately + Start parsing
   */
  processFileAndParse(file: File, index: number) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const previewUrl = file.type === 'application/pdf'
        ? this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(file))
        : e.target.result;

      const newItem: UploadedFileItem = { file, previewUrl, name: file.name };
      const control = this.entries.at(index)?.get('document');
      if (control) {
        control.setValue([newItem]);
        control.markAsDirty();
        control.setErrors(null);
      }
    };
    reader.readAsDataURL(file);

    // Submit to parser (async)
    this.submitDocumentForParsing(file, index);
  }

  /**
   * STEP 2: Submit document to parser API → Get jobId
   */
  private submitDocumentForParsing(file: File, index: number) {
    const level = this.entries.at(index)?.get('level')?.value;
    // The backend extracts the data best when doc_type is MARKSHEET for all education levels
    const docType = 'MARKSHEET';

    // ── Show the document-reading modal ──────────────────────────────────────
    this.docModalFileName = file.name;
    this.activeDocModalIndex = index;
    this.isDocModalVisible = true;

    setTimeout(() => {
      this.docReadingModal?.show(file.name);
    }, 0);
    // ─────────────────────────────────────────────────────────────────────────

    this.onboardingService.parseDocument(file, docType).subscribe({
      next: (response) => {
        const jobId = response.job_id;
        this.parseJobData[index] = {
          jobId,
          status: 'parsing',
          pollCount: 0
        };

        // Start polling
        this.startPollingForResult(jobId, index);
      },
      error: (err) => {
        console.error('Failed to submit document for parsing:', err);
        // Close modal and show error toast
        this.docReadingModal?.showError();
        this.parseJobData[index].status = 'error';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to submit document for analysis',
          life: 3000
        });
      }
    });
  }

  /**
   * STEP 3: Poll the parser API every 2 seconds until completed
   */
  private startPollingForResult(jobId: string, index: number) {
    const maxPolls = 30;
    this.parseJobData[index].pollCount = 0;

    const poll = () => {
      this.parseJobData[index].pollCount = (this.parseJobData[index].pollCount || 0) + 1;

      this.onboardingService.getDocumentParseResult(jobId).subscribe({
        next: (response) => {
          const status = response.status;

          if ((status === 'completed' || status === 'done') && response.result) {
            // SUCCESS — tell the modal all fields are found
            this.parseJobData[index].status = 'completed';
            this.parseJobData[index].extractedData = response.result;

            // Save temporarily until user clicks Continue
            this.pendingExtractedData = response.result;

            // Complete the modal (user clicks Continue to dismiss)
            if (this.activeDocModalIndex === index) {
              this.docReadingModal?.completeNow();
            }
          } else if (status === 'failed' || status === 'error') {
            // FAILED
            this.parseJobData[index].status = 'error';
            if (this.activeDocModalIndex === index) {
              this.docReadingModal?.showError();
            }
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to analyze document',
              life: 3000
            });
          } else {
            // Still processing - continue polling
            const currentPollCount = (this.parseJobData[index].pollCount || 0);
            if (currentPollCount < maxPolls) {
              const timeout = setTimeout(poll, 2000);
              this.pollTimeouts.push(timeout);
            } else {
              // Timeout
              this.parseJobData[index].status = 'error';
              if (this.activeDocModalIndex === index) {
                this.docReadingModal?.showError();
              }
              this.messageService.add({
                severity: 'warn',
                summary: 'Timeout',
                detail: 'Document analysis took too long. Please try again.',
                life: 3000
              });
            }
          }
        },
        error: (err) => {
          console.error('Poll failed:', err);
          const currentPollCount = (this.parseJobData[index].pollCount || 0);
          if (currentPollCount < maxPolls) {
            const timeout = setTimeout(poll, 2000);
            this.pollTimeouts.push(timeout);
          } else {
            this.parseJobData[index].status = 'error';
            if (this.activeDocModalIndex === index) {
              this.docReadingModal?.showError();
            }
          }
        }
      });
    };

    // Start first poll after 1 second
    const timeout = setTimeout(poll, 1000);
    this.pollTimeouts.push(timeout);
  }

  // ── Modal event handlers ──────────────────────────────────────────────────
  onDocModalComplete(): void {
    this.isDocModalVisible = false;

    // Auto fill form only when user clicks Continue
    if (this.activeDocModalIndex >= 0 && this.pendingExtractedData) {
      this.autoFillFormFromParsedData(this.activeDocModalIndex, this.pendingExtractedData);
    }

    this.pendingExtractedData = null;
    this.activeDocModalIndex = -1;
    // Success toast after user clicks Continue
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Document analyzed successfully! Fields have been auto-filled.',
      life: 3000
    });
  }

  onDocModalCancel(): void {
    // Simply discard the extracted data without touching the form
    this.pendingExtractedData = null;
    this.isDocModalVisible = false;
    this.activeDocModalIndex = -1;
  }

  onDocModalClose(): void {
    this.isDocModalVisible = false;
    // NOTE: Do not clear pendingExtractedData or activeDocModalIndex here,
    // because linkdindetails-fetch emits onClose() right before onComplete() and onCancel().
    // Those methods will handle their own state cleanup.
  }
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * STEP 4: Auto-fill form fields with parsed data
   */
 private autoFillFormFromParsedData(index: number, extractedData: any) {
  const entry = this.entries.at(index);
  if (!entry) return;

  const level = entry.get('level')?.value;

  // Helper: sirf tab set karo jab field empty ho
  const setIfEmpty = (controlName: string, value: any) => {
    if (value === null || value === undefined || value === '') return;
    const ctrl = entry.get(controlName);
    if (!ctrl) return;
    const existing = ctrl.value;
    const isEmpty = existing === null || existing === undefined || existing === '' || 
                    (Array.isArray(existing) && existing.length === 0);
    if (isEmpty) {
      ctrl.setValue(value);
    }
  };

  // === SUBJECTS — MERGE (existing + new) ===
  const chips: any[] = [];
  let subjectsArray = extractedData.subjects || extractedData.subject_and_grades || extractedData.SubjectsAndGrades;
  if (Array.isArray(subjectsArray)) {
    subjectsArray.forEach((sg: any) => {
      const subName = sg.subject_name || sg.SubjectName || sg.subject || '';
      const marks = sg.marks_obtained !== undefined ? sg.marks_obtained : sg.marks;
      const grade = sg.grade || sg.Grade || '';
      let gradeStr = marks !== undefined ? marks.toString() : '';
      if (grade) gradeStr = gradeStr ? `${gradeStr} (${grade})` : grade;
      if (subName || gradeStr) chips.push({ SubjectName: subName, Grade: gradeStr });
    });
  } else {
    const subjectsObj = extractedData.subjects || extractedData;
    if (subjectsObj && typeof subjectsObj === 'object') {
      Object.keys(subjectsObj).forEach(key => {
        const val = subjectsObj[key];
        if (val && typeof val === 'object' && !Array.isArray(val) && key !== 'subjects') {
          const marks = val.marks_obtained !== undefined ? val.marks_obtained : val.marks;
          const grade = val.grade || val.Grade;
          if (marks !== undefined || grade !== undefined) {
            let gradeStr = marks !== undefined ? marks.toString() : '';
            if (grade) gradeStr = gradeStr ? `${gradeStr} (${grade})` : grade;
            chips.push({ SubjectName: key, Grade: gradeStr });
          }
        }
      });
    }
  }

  if (chips.length > 0) {
    const existing = entry.get('subjectsGrades')?.value || [];
    entry.get('subjectsGrades')?.setValue([...existing, ...chips]);
  }

 // === PERCENTAGE ===
let percentageStr = extractedData.percentage || extractedData.overall_percentage || extractedData.total_grade;
if (percentageStr) {
  if (typeof percentageStr === 'string') percentageStr = percentageStr.replace('%', '').trim();
  entry.get('percentage')?.setValue(percentageStr);  // setIfEmpty HATAO, seedha setValue
}

// === YEARS ATTENDED ===
if (extractedData.exam_year) {
  const toYear = parseInt(extractedData.exam_year, 10);
  if (!isNaN(toYear)) {
    entry.get('attendedTo')?.setValue(toYear);        // seedha setValue
    entry.get('attendedFrom')?.setValue(toYear - 1); // seedha setValue
  }
}

// === LEVEL SPECIFIC ===
if (level === EducationalLevelEnum.Bachelors || level === EducationalLevelEnum.Masters) {
  if (extractedData.university_name) entry.get('universityName')?.setValue(extractedData.university_name);
  if (extractedData.degree_name) entry.get('degreeName')?.setValue(extractedData.degree_name);
  if (extractedData.major) entry.get('majors')?.setValue(extractedData.major);
} else {
  if (extractedData.school_name) entry.get('schoolName')?.setValue(extractedData.school_name);

  // Board — hamesha overwrite if API se aaya
  if (extractedData.exam_type) {
    const matchingBoard = this.boardOptions.find(b =>
      b.label.toLowerCase().includes(extractedData.exam_type.toLowerCase())
    );
    if (matchingBoard) entry.get('boardType')?.setValue(matchingBoard.value);
  }
  if (extractedData.board) {
    const shortCode = extractedData.board.trim().toUpperCase();
    let boardMatch = this.boardOptions.find(b => b.label.toUpperCase().includes(shortCode));
    if (!boardMatch) {
      boardMatch = this.boardOptions.find(b => {
        const m = b.label.match(/\(([^)]+)\)/);
        return m && m[1].toUpperCase() === shortCode;
      });
    }
    if (boardMatch) entry.get('boardType')?.setValue(boardMatch.value);
  }

  }

  entry.markAsDirty();
}

  // ============================================
  // FILE HANDLING METHODS
  // ============================================

  triggerAddMore(index: number) {
    this.currentUploadIndex = index;
    if (this.hiddenFileInput) {
      this.hiddenFileInput.nativeElement.click();
    }
  }

  onHiddenFileChange(event: any) {
    const files = event.target.files;
    if (files && files.length > 0 && this.currentUploadIndex >= 0) {
      Array.from(files).forEach((file: any) => {
        this.processFile(file as File, this.currentUploadIndex);
      });
    }
    event.target.value = '';
  }

  processFile(file: File, index: number) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const previewUrl = file.type === 'application/pdf'
        ? this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(file))
        : e.target.result;

      const newItem: UploadedFileItem = { file, previewUrl, name: file.name };
      const control = this.entries.at(index)?.get('document');
      if (control) {
        control.setValue([newItem]);
        control.markAsDirty();
        control.setErrors(null);
      }
    };
    reader.readAsDataURL(file);
  }

  removeFileObj(entryIndex: number) {
    const entry = this.entries.at(entryIndex);
    const control = entry?.get('document');
    if (control) {
      control.setValue([]);
      control.markAsTouched();
      control.markAsDirty();
      control.updateValueAndValidity();
    }
    // Reset parsing status
    if (this.parseJobData[entryIndex]) {
      this.parseJobData[entryIndex].status = 'pending';
    }
  }

  getUploadedFiles(index: number): UploadedFileItem[] {
    const val = this.entries.at(index)?.get('document')?.value;
    return Array.isArray(val) ? val : [];
  }

  hasFiles(index: number): boolean {
    return this.getUploadedFiles(index).length > 0;
  }

  openPreview(item: UploadedFileItem) {
    this.selectedPreviewItem = item;
    this.isPreviewLoading = true;
    this.safePdfUrl = null;

    const isPdf = this.isPdf(item);
    const isImage = this.isImage(item);

    if (isPdf) {
      const url = item.previewUrl;
      if (typeof url === 'string') {
        if (url.startsWith('http')) {
          const urlStr = encodeURIComponent(url);
          this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`https://docs.google.com/viewer?url=${urlStr}&embedded=true`);
        } else {
          this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        }
      } else {
        this.safePdfUrl = url;
      }
    }

    this.displayPreviewDialog = true;
  }

  onPreviewLoad() {
    this.isPreviewLoading = false;
  }

  isImage(item: UploadedFileItem): boolean {
    if (item.file) {
      return item.file.type.startsWith('image/');
    }
    const url = typeof item.previewUrl === 'string' ? item.previewUrl : '';
    return /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(url.split('?')[0]);
  }

  isPdf(item: UploadedFileItem): boolean {
    if (item.file) {
      return item.file.type === 'application/pdf';
    }
    const url = typeof item.previewUrl === 'string' ? item.previewUrl : '';
    return /\.pdf$/i.test(url.split('?')[0]);
  }

  getDocumentDisplayName(index: number): string {
    const level = this.entries.at(index)?.get('level')?.value;
    switch (level) {
      case EducationalLevelEnum.Grade10: return 'Grade 10 Marksheet';
      case EducationalLevelEnum.Grade11: return 'Grade 11 Marksheet';
      case EducationalLevelEnum.Grade12: return 'Grade 12 Marksheet';
      case EducationalLevelEnum.Bachelors: return 'Degree Certificate/Transcript';
      case EducationalLevelEnum.Masters: return 'Masters Degree Certificate/Transcript';
      default: return 'Education Document';
    }
  }

  getLevelLabel(val: any): string {
    const opt = this.levelOptions.find(o => o.value === val);
    return opt ? opt.label : '';
  }

  getAvailableLevelOptions(index: number) {
    const selected = this.entries.controls
      .map((g, i) => i === index ? null : g.get('level')?.value)
      .filter(v => v !== null && v !== undefined);

    return this.levelOptions.filter(o => !selected.includes(o.value));
  }
}